import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, of, tap } from "rxjs";
import { GraphqlService, GraphQlObservable } from "@ubisolutions/core";
import { ResolvedTenantConfigGraphql } from "@workspace/pages/maps/asset-tracking-map/asset-tracking-map.graphql";
import {
    ResetCurrentClientConfigGraphql,
    SetCurrentClientConfigGraphql,
} from "@shared/business/services/client-settings.service.graphql";
import { logger } from "@shared/base/utils/logger";

const TRANSLATIONS_KEY = "ui.translations";
const ICONS_KEY = "ui.icons";
const DISPLAY_ATTRIBUTES_KEY = "ui.asset.display-attributes";

/**
 * Typed access to the current client's UI settings (translations, icons, asset display
 * attributes), backed by the current-scope `resolvedTenantConfig` / `setCurrentClientConfig` /
 * `resetCurrentClientConfig` GraphQL operations. Replaces the old `UiClientService`, which read a
 * single `UiClient` row directly.
 *
 * Each setting is fetched once and cached (via `GraphqlQuery#observeWith`'s built-in
 * `shareReplay`); a successful write refreshes only the affected cached key.
 */
@Injectable({
    providedIn: "root",
})
export class ClientSettingsService {
    private readonly graphqlService = inject(GraphqlService);

    private readonly translations$$ = this.graphqlService.singleQuery(ResolvedTenantConfigGraphql)
        .observeWith(of({ configKey: TRANSLATIONS_KEY }));

    private readonly icons$$ = this.graphqlService.singleQuery(ResolvedTenantConfigGraphql)
        .observeWith(of({ configKey: ICONS_KEY }));

    private readonly displayAttributes$$ = this.graphqlService.singleQuery(ResolvedTenantConfigGraphql)
        .observeWith(of({ configKey: DISPLAY_ATTRIBUTES_KEY }));

    getTranslations(): Observable<any | null> {
        return this.parseValue$(this.translations$$);
    }

    getIcons(): Observable<any | null> {
        return this.parseValue$(this.icons$$);
    }

    getDisplayAttributes(): Observable<any | null> {
        return this.parseValue$(this.displayAttributes$$);
    }

    checkSchemaDisplay(key: string): Observable<boolean | null> {
        return this.getDisplayAttributes().pipe(
            map((json) => {
                if (json == null) {
                    return null;
                }
                const keys = key.split(".");
                let value: any = json;
                for (const k of keys) {
                    if (value == null || !Object.prototype.hasOwnProperty.call(value, k)) {
                        logger?.warn(`Key "${k}" doesn't exist in JSON.`);
                        return null;
                    }
                    value = value[k];
                }
                return Boolean(value);
            }),
            catchError((err) => {
                logger?.error(`Error resolving schema display for key "${key}": ${err}`);
                return of(null);
            }),
        );
    }

    getIconForKey(key: string): Observable<string> {
        return this.getIcons().pipe(
            map((json) => {
                if (json == null) {
                    return this.getDefaultIcon(key);
                }
                const value = key.split(".").reduce((acc: any, part: string) => acc?.[part], json);
                return value !== undefined ? value : this.getDefaultIcon(key);
            }),
            catchError(() => of(this.getDefaultIcon(key))),
        );
    }

    setTranslations(value: unknown) {
        return this.setValue(TRANSLATIONS_KEY, value, this.translations$$);
    }

    setIcons(value: unknown) {
        return this.setValue(ICONS_KEY, value, this.icons$$);
    }

    setDisplayAttributes(value: unknown) {
        return this.setValue(DISPLAY_ATTRIBUTES_KEY, value, this.displayAttributes$$);
    }

    resetTranslations() {
        return this.resetValue(TRANSLATIONS_KEY, this.translations$$);
    }

    resetIcons() {
        return this.resetValue(ICONS_KEY, this.icons$$);
    }

    resetDisplayAttributes() {
        return this.resetValue(DISPLAY_ATTRIBUTES_KEY, this.displayAttributes$$);
    }

    private setValue(configKey: string, value: unknown, cache: GraphQlObservable<any>) {
        return this.graphqlService.mutate(SetCurrentClientConfigGraphql, {
            configKey,
            configValue: JSON.stringify(value),
        }).pipe(tap(() => cache.refresh()));
    }

    private resetValue(configKey: string, cache: GraphQlObservable<any>) {
        return this.graphqlService.mutate(ResetCurrentClientConfigGraphql, { configKey }).pipe(
            tap(() => cache.refresh()),
        );
    }

    private parseValue$(source$: GraphQlObservable<{ value?: string | null } | null>): Observable<any | null> {
        return source$.pipe(
            map(result => result?.value ?? null),
            map((raw) => {
                if (!raw) {
                    return null;
                }
                try {
                    return JSON.parse(raw);
                } catch (err) {
                    logger?.error(`Error parsing client setting JSON: ${err}`);
                    return null;
                }
            }),
            catchError(() => of(null)),
        );
    }

    private getDefaultIcon(key: string): string {
        if (key.includes("asset")) {
            return "inventory_2";
        } else if (key.includes("logisticalCenter")) {
            return "home_work";
        } else {
            return key;
        }
    }
}
